<template>
  <Component v-if="showComponent" :is="componentType"
             target="_blank" :href="certificate?.path" rel="noopener noreferrer"
             :class="containerClasses"
             class="flex max-w-fit items-center gap-4 rounded-md border border-slate-200 group/certificate">
    <img v-if="type === TYPE_PDF" src="https://resources.businessdraft.com/pdf-file-icon.svg" alt="PDF Icon"
         :class="small ? 'h-8 w-5 py-1.5' : 'h-8 w-8'"/>
    <PhotoIcon v-else-if="type === TYPE_IMAGE" class="h-8 w-5 text-slate-600 py-1.5"/>
    <PaperClipIcon v-else class="h-8 w-5 text-slate-600 py-1.5"/>
    <Component :is="subLinkComponent" target="_blank" :href="certificate?.path" rel="noopener noreferrer"
               class="flex flex-col" :class="small ? 'mr-1' : 'mr-2'">
      <span class="block text-sm font-medium text-slate-600">{{ certificate?.title || "Untitled Certificate" }}</span>
      <span v-if="hasPath && !small"
            class="flex items-center text-xs group-hover/certificate:underline space-x-1 text-primary-500 group-hover/certificate:text-primary-600">
        <span v-if="hasDataUrlPath">
          File Attached
        </span>
        <template v-else>
          <span>
          View Attached
          <span v-if="type === TYPE_PDF">PDF</span>
          <span v-else-if="type === TYPE_IMAGE">Image</span>
          <span v-else>File</span>
        </span>
        <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"
             class="h-3 w-3 transition-all group-hover/certificate:translate-x-0.5">
          <polygon fill="currentColor"
                   points="5 4.31 5 5.69 9.33 5.69 2.51 12.51 3.49 13.49 10.31 6.67 10.31 11 11.69 11 11.69 4.31 5 4.31"/>
        </svg>
        </template>
      </span>
      <span v-else-if="!small" class="block text-xs text-slate-400">
        No File Attached
      </span>
    </Component>
    <template v-if="editable">
      <HoverTooltip tip="Edit Item" placement="top" class="2xl:ml-2">
        <FormButton @click="setIsEditing(true)" color="green" size="xs">
          <PencilSquareIcon class="h-4 w-4"/>
        </FormButton>
      </HoverTooltip>
      <HoverTooltip tip="Remove Item" placement="top" class="-ml-2">
        <FormButton color="red" size="xs" @click="setConfirmRemove(true)">
          <TrashIcon class="h-4 w-4"/>
        </FormButton>
      </HoverTooltip>
      <EditableCandidateCertificatePreviewModal v-model:open="isEditing" :certificate="certificate"
                                                @update:certificate="updateCertificate"/>
      <ModalConfirm v-model:open="confirmRemove"
                    title="Are you sure you want to remove this item?"
                    level="danger"
                    description="There is no undo – You'll have to re-add it if you want it again."
                    proceed-button-text="Remove Entry"
                    class="z-max"
                    @confirm="deleteItem"/>
    </template>
  </Component>
</template>

<script setup>
import EditableCandidateCertificatePreviewModal from "./EditableCandidateCertificatePreviewModal.vue";
import FormButton from "./FormButton.vue";
import HoverTooltip from "./HoverTooltip.vue";
import ModalConfirm from "./ModalConfirm.vue";
import {computed, ref} from "vue";
import {PaperClipIcon, PhotoIcon} from "@heroicons/vue/24/outline";
import {PencilSquareIcon, TrashIcon} from "@heroicons/vue/24/solid/index.js";

const emits = defineEmits(["delete", "update:certificate"]);
const props = defineProps({
  certificate: Object,
  small: Boolean,
  editable: Boolean,
});

const TYPE_PDF = "pdf";
const TYPE_IMAGE = "image";

const isEditing = ref(false);
const confirmRemove = ref(false);

const type = computed(() => {
  if (!props.certificate?.path) return null;
  if (props.certificate.path.endsWith(".pdf") || props.certificate.path.startsWith("data:application/pdf")) return TYPE_PDF;
  return TYPE_IMAGE;
});

const hasPath = computed(() => !!props.certificate?.path);
const hasTitle = computed(() => !!props.certificate?.title);
const showComponent = computed(() => props.editable || hasPath.value || hasTitle.value);
const containerClasses = computed(() => props.small ? "px-3 py-1" : "px-4 py-3");

const componentType = computed(() => {
  if (hasPath.value && !props.editable) return "a";
  return "div";
});

const subLinkComponent = computed(() => {
  if (hasPath.value && props.editable && !hasDataUrlPath.value) return "a";
  return "span";
});

const hasDataUrlPath = computed(() => props.certificate?.path?.startsWith("data:"));

const setIsEditing = (value) => isEditing.value = value;
const setConfirmRemove = (value) => confirmRemove.value = value;
const deleteItem = () => emits("delete");
const updateCertificate = (certificate) => emits("update:certificate", certificate);
</script>
